import FormCompanyImage from 'components/form/FormCompanyImage';
import FormSelect from 'components/form/FormSelect';
import FormText from 'components/form/FormText';
import FormWysiwygEditor from 'components/form/FormWysiwygEditor';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { QuizEditFormStateType } from 'types/page/quizEditFormStateType';

/**
 *　クイズ作成編集 判定結果コンポーネント
 */
export default function WsQuizEditResults(props: { readOnly: boolean }): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const {
    control,
    register,
    getValues,
    formState: { errors },
  } = useFormContext<QuizEditFormStateType>();

  const { append, move, remove, fields } = useFieldArray({
    control,
    name: `results`,
  });

  // 登録可能最大件数
  const limit = 10;

  const scoreThresholdOption = [
    { value: 1, label: '1問' },
    { value: 2, label: '2問' },
    { value: 3, label: '3問' },
    { value: 4, label: '4問' },
    { value: 5, label: '5問' },
    { value: 6, label: '6問' },
    { value: 7, label: '7問' },
    { value: 8, label: '8問' },
    { value: 9, label: '9問' },
    { value: 10, label: '10問' },
    { value: 11, label: '11問' },
    { value: 12, label: '12問' },
    { value: 13, label: '13問' },
    { value: 14, label: '14問' },
    { value: 15, label: '15問' },
    { value: 16, label: '16問' },
    { value: 17, label: '17問' },
    { value: 18, label: '18問' },
    { value: 19, label: '19問' },
    { value: 20, label: '20問' },
    { value: 21, label: '21問' },
    { value: 22, label: '22問' },
    { value: 23, label: '23問' },
    { value: 24, label: '24問' },
    { value: 25, label: '25問' },
  ];
  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 設問の順番を入れ替えます。
   */
  const onMove = (questionIndex: number, upper: boolean) => {
    // 先頭の場合は上に移動不可
    if (upper && questionIndex === 0) {
      return;
    }
    // 最終行の場合は下に移動不可
    if (!upper && questionIndex === fields.length - 1) {
      return;
    }
    // 要素の入れ替え
    move(questionIndex, upper ? questionIndex - 1 : questionIndex + 1);
  };

  /**
   * 設問を削除します。
   */
  const onRemove = (questionIndex: number) => {
    // 1件以下の場合は削除不可
    if (fields.length <= 1) {
      return;
    }
    // 要素の削除
    remove(questionIndex);
  };

  /**
   * 設問を追加します。
   */
  const onAppend = () => {
    // 10件以下の場合は削除不可
    if (fields.length >= limit) {
      return;
    }
    // 要素の追加
    append({});
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      <section className="section-form-default section-answer-result">
        <div className="container">
          <div className="box-common">
            <h3 className="title-section">回答結果ページ</h3>

            {getValues()?.results.map((result, resultIndex) => {
              return (
                <div key={`ws-quiz-edit-results-${resultIndex}`} className="section-body">
                  <div className="form-default-container">
                    <div className="row row-0">
                      <div className="col-md col-header pt-md-0">
                        <div className="form-label mb-lg">結果パターン{resultIndex + 1}</div>

                        <div className="card form-header">
                          <h4 className="title">回答結果用のメイン画像</h4>

                          <FormCompanyImage
                            control={control}
                            name={`results.${resultIndex}.imageKey`}
                            readOnly={props.readOnly}
                          />
                        </div>
                      </div>
                      <div className="col-md col-form ps-md-4 ms-md-2">
                        <div className="btn-group-header text-end" style={{ marginTop: '0' }}>
                          <div className="btn-group">
                            {resultIndex > 0 && (
                              <>
                                <button
                                  className="btn"
                                  onClick={() => onMove(resultIndex, true)}
                                  disabled={props.readOnly || resultIndex === 1}
                                >
                                  <img src="/img/icon/arrow-up-black.svg" alt="" />
                                </button>
                                <button
                                  className="btn"
                                  onClick={() => onMove(resultIndex, false)}
                                  disabled={props.readOnly}
                                >
                                  <img
                                    src="/img/icon/arrow-up-black.svg"
                                    alt=""
                                    className="scaley-1"
                                  />
                                </button>
                                <button
                                  className="btn"
                                  onClick={() => onRemove(resultIndex)}
                                  disabled={props.readOnly}
                                >
                                  <img src="/img/icon/trash.svg" alt="" className="filter-black" />
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="form-label">表示する条件</div>
                        <div className="form-group form-group-row align-items-center mb-md-4">
                          {resultIndex === 0 && (
                            <>
                              <input
                                type="text"
                                className="form-control"
                                style={{ width: '80px', fontWeight: 'bold' }}
                                defaultValue="0問"
                              ></input>
                              <input
                                type="hidden"
                                {...register(`results.${resultIndex}.scoreThreshold`, {})}
                              />
                            </>
                          )}
                          {resultIndex >= 1 && (
                            <FormSelect
                              name={`results.${resultIndex}.scoreThreshold`}
                              register={register}
                              errors={errors}
                              options={scoreThresholdOption}
                              disabled={props.readOnly}
                            />
                          )}
                          <p className="ms-3 mb-0">以上正解した場合</p>
                        </div>
                        <div className="form-group mb-md-4">
                          <FormText
                            subject="見出し"
                            name={`results.${resultIndex}.title`}
                            placeholder="見出しを入れましょう"
                            register={register}
                            errors={errors}
                            readOnly={props.readOnly}
                            maxLength={100}
                          />
                        </div>
                        <div className="form-group">
                          <FormWysiwygEditor
                            control={control}
                            subject="本文"
                            name={`results.${resultIndex}.descriptionHtml`}
                            placeholder="すべての回答を終えたユーザーに向けた説明文を入れましょう。画像やリンクなども貼り付けることができます。"
                            toolbar={['image', 'link', 'button']}
                            errors={errors}
                            readOnly={props.readOnly}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="max-width-238 ms-auto">
              <button
                onClick={onAppend}
                disabled={fields.length >= limit || props.readOnly}
                className="btn btn-common moderate-blue w-100"
              >
                条件を追加する
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
